import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { LogIn, Loader2, Eye, EyeOff, Mail, Lock, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';

export default function Login() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const from = location.state?.from || '/mon-compte';

  const [form, setForm] = useState({ email: '', password: '' });
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.password) {
      toast({ title: 'Champs manquants', description: 'Email et mot de passe requis.' });
      return;
    }
    setLoading(true);
    try {
      await login(form.email.trim().toLowerCase(), form.password);
      toast({ title: 'Connecté !', description: 'Bienvenue dans la ruche.' });
      navigate(from, { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.detail || 'Impossible de se connecter.';
      if (status === 403) {
        toast({ title: 'Compte non vérifié', description: 'Vérifiez votre email pour activer votre compte.' });
        navigate(`/verification?email=${encodeURIComponent(form.email.trim())}`);
      } else {
        toast({ title: 'Connexion échouée', description: typeof msg === 'string' ? msg : 'Erreur.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    'w-full pl-11 pr-4 py-3 rounded-md bg-neutral-950/80 border border-white/10 text-white placeholder-neutral-500 focus:outline-none focus:border-yellow-400/60 focus:ring-2 focus:ring-yellow-400/20 transition-colors';

  return (
    <div>
      <section className="relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(250,204,21,0.15),transparent_60%)]" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 py-16 text-center">
          <div className="inline-flex items-center gap-2 mb-5 px-4 py-1.5 rounded-full border border-yellow-400/30 bg-yellow-400/5 text-yellow-300 text-xs font-medium tracking-wider uppercase">
            <Sparkles size={14} /> Connexion
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-yellow-400 leading-[1.05]">Content de te revoir</h1>
          <p className="mt-4 text-neutral-300 text-base max-w-xl mx-auto">Connecte-toi à ton compte BuzzBump pour suivre l'avancement du projet.</p>
        </div>
      </section>

      <section className="max-w-md mx-auto px-6 lg:px-10 pb-20">
        <form onSubmit={onSubmit} className="p-7 md:p-9 rounded-2xl border border-white/10 bg-neutral-900/40 space-y-5">
          <div>
            <label className="block text-sm text-neutral-300 mb-2">Email</label>
            <div className="relative">
              <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500" />
              <input
                type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={inputCls} placeholder="vous@exemple.com"
                maxLength={120} autoComplete="email" disabled={loading}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm text-neutral-300 mb-2">Mot de passe</label>
            <div className="relative">
              <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500" />
              <input
                type={show ? 'text' : 'password'} required value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className={`${inputCls} pr-12`} placeholder="Votre mot de passe"
                maxLength={200} autoComplete="current-password" disabled={loading}
              />
              <button type="button" onClick={() => setShow((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-yellow-400"
                aria-label="Afficher / masquer le mot de passe">
                {show ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button type="submit" disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-md bg-yellow-400 text-neutral-900 font-semibold hover:bg-yellow-300 transition-colors disabled:opacity-60">
            {loading ? <><Loader2 size={18} className="animate-spin" /> Connexion...</> : <><LogIn size={18} /> Se connecter</>}
          </button>

          <div className="pt-2 text-center text-xs text-neutral-500">
            Pas encore inscrit ? <Link to="/inscription" className="text-yellow-400 hover:underline">Créer un compte</Link>
          </div>
        </form>
      </section>
    </div>
  );
}
